import React, { useRef, useState, useEffect } from "react";
import clone from "lodash.clonedeep";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
    FormGroup, ControlLabel, Checkbox, Button, FormControl, Glyphicon,
    Modal, Row, Col, ListGroupItem, ButtonGroup
} from "react-bootstrap";
import { partnerCreate, partnerDelete, partnerGetList, partnersOrderUpdate, partnerUpdate } from "../../../../../../api/cp/partners";
import FormControlLanguage from "../../../../UI/FormControlLanguage";
import MoveIcon from "../../../Widgets/components/Icons/MoveIcon";
import locale from "../../../../../locale";
import Loading from "../../../../Loading";

const newPartner = (partners) => ({
    order: partners.reduce((order, cur) => cur.order > order ? cur.order : order, 0) + 1,
    imageUrl: '',
    color: '000000',
    partnerSiteUrl: '',
    nameRu: '',
    nameEn: '',
});

const PartnerEditModal = ({ locale, partner, handleSave, handleChange, handleSaveFile }) => {
    const imageInputRef = useRef(null);
    
    if (!partner) return null;

    return <>
        <Modal.Header closeButton>
            <Modal.Title>{locale('settings.partner.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <FormGroup>
                <Row>
                    <Col xs={12}>
                        <ControlLabel>{locale('settings.partner.name')}</ControlLabel>
                        <FormControlLanguage
                            type="text"
                            values={{
                                ru: partner.nameRu,
                                en: partner.nameEn,
                            }}
                            onChange={{
                                ru: (e) => handleChange('nameRu', e.target.value),
                                en: (e) => handleChange('nameEn', e.target.value),
                            }}
                            onBlur={() => handleSave({ nameRu: partner.nameRu, nameEn: partner.nameEn })}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <ControlLabel>{partner.logoImageUrl ? "Изменить" : "Добавить"} {locale('settings.partner.logo')}</ControlLabel>
                        {partner.logoImageUrl && <div><img src={partner.logoImageUrl} style={{ maxWidth: '100%', maxHeight: 120 }} alt="" /></div>}
                        <div>
                            <Button onClick={() => imageInputRef.current?.click()}><Glyphicon glyph="folder-open" /></Button>
                            {' '}
                            {partner.logoImageUrl &&
                                <Button onClick={() => handleSaveFile('logoImage', 'DELETE')} bsStyle="danger">
                                    <Glyphicon glyph="trash" />
                                </Button>
                            }
                        </div>
                        <FormControl
                            type="file"
                            onChange={(e) => handleSaveFile('logoImage', e.target.files[0] || null)}
                            inputRef={ref => imageInputRef.current = ref}
                            style={{ display: 'none' }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <ControlLabel>{locale('settings.partner.backgroundColor')}</ControlLabel>
                        <FormControl
                            type="color"
                            value={'#' + partner.backgroundColor}
                            onChange={({ target }) => handleChange('backgroundColor', target.value.replace('#', ''))}
                            onBlur={() => handleSave({ backgroundColor: partner.backgroundColor })}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                    <ControlLabel>{locale('settings.partner.url')}</ControlLabel>
                        <FormControl
                            type="text"
                            value={partner.partnerSiteUrl}
                            onChange={(e) => handleChange('partnerSiteUrl', e.target.value)}
                            onBlur={() => handleSave({ partnerSiteUrl: partner.partnerSiteUrl })}
                        />
                    </Col>
                </Row>
            </FormGroup>
        </Modal.Body>
    </>
}

export default ({ event, block, blockProperties, onUpdateBlockProperties, onSave, onSaveFile }) => {
    const imageInputRef = useRef({});
    const [partners, setPartners] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);

    const getPartners = async () => {
        setIsLoading(true);
        try {
            const partners = await partnerGetList(event.eventPassword, block.id);
            setPartners(partners.sort((a, b) => a.order - b.order));
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getPartners();
    }, []);

    const handleUpdatePartnersOrder = async (list) => {
        setPartners(list);

        try {
            const payload = list.map(({ id }) => id);
            const response = await partnersOrderUpdate(event.eventPassword, block.id, { sortOrder: payload });
            setPartners(response.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error(error);
        }
    }

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const _partners = clone(partners);
        const [removed] = _partners.splice(result.source.index, 1);
        _partners.splice(result.destination.index, 0, removed);
        handleUpdatePartnersOrder(_partners);
    }

    const handleDeletePartner = async (partnerId) => {
        await partnerDelete(event.eventPassword, partnerId);
        const _partners = partners.filter(({ id }) => id !== partnerId);
        setPartners(_partners);
    }

    const handleSavePartner = async (properties) => {
        if (!editingPartner) return;
        const response = await partnerUpdate(event.eventPassword, editingPartner.id, properties);
        const _result = partners.map((partner) => partner.id === editingPartner.id ? response : partner);
        setEditingPartner(response);
        setPartners(_result);
    }

    const handlePartnerUpdateFile = async (key, file) => {
        const formData = new FormData();
        formData.append(key, file);
        const response = await partnerUpdate(event.eventPassword, editingPartner.id, formData);
        const _result = partners.map((partner) => partner.id === editingPartner.id ? response : partner);
        setEditingPartner(response);
        setPartners(_result);
    }

    const handleChangePartner = (key, value) => {
        setEditingPartner(partner => ({ ...partner, [key]: value }));
    }

    const addPartner = async () => {
        try {
            const partner = newPartner(partners);
            const response = await partnerCreate(event.eventPassword, block.id, partner);
            setPartners(partners  => [...partners, response]);
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <>
            <FormGroup>
                <ControlLabel>{locale('settings.title')}</ControlLabel>
                <FormControlLanguage
                    type="text"
                    values={{
                        ru: blockProperties.titleRu,
                        en: blockProperties.titleEn,
                    }}
                    onChange={{
                        ru: (e) => onUpdateBlockProperties({ titleRu: e.target.value }),
                        en: (e) => onUpdateBlockProperties({ titleEn: e.target.value }),
                    }}
                    onBlur={() => onSave({ titleRu: blockProperties.titleRu, titleEn: blockProperties.titleEn })}
                />
            </FormGroup>

            <FormGroup>
                <ControlLabel>{locale('settings.subtitle')}</ControlLabel>
                <Checkbox
                    checked={blockProperties?.showSubtitle}
                    onChange={(e) => onSave({ showSubtitle: e.target.checked })}
                >
                    {locale('settings.showSubtitle')}
                </Checkbox>
            </FormGroup>
            {blockProperties.showSubtitle && (
                <FormGroup>
                    <FormControlLanguage
                        type="text"
                        values={{
                            ru: blockProperties.subtitleRu,
                            en: blockProperties.subtitleEn,
                        }}
                        onChange={{
                            ru: (e) => onUpdateBlockProperties({ subtitleRu: e.target.value }),
                            en: (e) => onUpdateBlockProperties({ subtitleEn: e.target.value }),
                        }}
                        onBlur={() => onSave({ subtitleRu: blockProperties.subtitleRu, subtitleEn: blockProperties.subtitleEn })}
                    />
                </FormGroup>
            )}

            <FormGroup>
                <ControlLabel>{block.imageUrl ? "Изменить" : "Добавить"} Фон</ControlLabel>
                {block.imageUrl && <div><img src={block.imageUrl} style={{ maxWidth: '100%', maxHeight: 120 }} alt="" /></div>}
                <div>
                    <Button onClick={() => imageInputRef.current?.click()}><Glyphicon glyph="folder-open" /></Button>
                    {' '}
                    {block.imageUrl && <Button onClick={() => onSaveFile('image', 'DELETE')} bsStyle="danger"><Glyphicon glyph="trash" /></Button>}
                </div>
                <FormControl
                    type="file"
                    onChange={(e) => onSaveFile('image', e.target.files[0] || null)}
                    inputRef={ref => imageInputRef.current = ref}
                    style={{ display: 'none' }}
                />
            </FormGroup>

            <ControlLabel>{locale('settings.blocks.partners')}</ControlLabel>
            {isLoading && <Loading />}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {partners?.map((partner, index) => (
                                <Draggable
                                    key={partner.id}
                                    draggableId={`${partner.id || index}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                left: 0,
                                                top: provided.draggableProps.style?.top - 22 || 0
                                            }}
                                        >
                                            <ListGroupItem
                                                key={partner.id}
                                                style={{
                                                    padding: '4px 4px 4px 12px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    height: '44px',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', gap: '0.5rem' }}>
                                                    <MoveIcon />
                                                    <div
                                                        style={{
                                                            width: 100,
                                                            height: 36,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: 4,
                                                            backgroundColor: `#${partner.backgroundColor}`
                                                        }}
                                                    >
                                                        {partner.logoImageUrl && (
                                                            <img
                                                                src={partner.logoImageUrl}
                                                                style={{ maxWidth: '100%', maxHeight: 32 }}
                                                                alt={partner.nameRu || partner.nameEn}
                                                            />
                                                        )}
                                                    </div>
                                                    <span
                                                        style={{
                                                            marginLeft: '0.5rem',
                                                            fontSize: '0.85rem',
                                                            opacity: 0.85,
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: 90,
                                                        }}
                                                    >
                                                        {partner.partnerSiteUrl}
                                                    </span>
                                                </div>
                                                <ButtonGroup>
                                                    <Button onClick={() => setEditingPartner(clone(partner))}>
                                                        <Glyphicon glyph="edit" />
                                                    </Button>
                                                    <Button bsStyle="danger"><Glyphicon glyph="trash" onClick={() => handleDeletePartner(partner.id)} /></Button>
                                                </ButtonGroup>
                                            </ListGroupItem>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <Button
                bsSize="large"
                style={{ width: "100%" }}
                onClick={addPartner}
            >
                {locale('settings.partners.addPartner')}
            </Button>

            <Modal
                show={!!editingPartner}
                onHide={() => setEditingPartner(null)}
            >
                <PartnerEditModal
                    locale={locale}
                    partner={editingPartner}
                    handleSave={handleSavePartner}
                    handleChange={handleChangePartner}
                    handleSaveFile={handlePartnerUpdateFile}
                />
            </Modal>
        </>
    )
};